import {
  API_BASE_URL,
  buildQueryString,
  fetchWithTimeout,
  handleResponse,
} from "./shared";

// ============================================================================
// Block Accounts API (SFWL-012 — super-admin block/SMA management)
// ============================================================================

/**
 * Entire `/api/v1/admin/block-accounts*` surface is `SovrunAdminGuard`-gated
 * server-side (platform-role, not org/tenant scoped) — see the locked spec
 * (OQ4). Callers pass `tenantId` explicitly as a filter, distinct from the
 * `x-tenant-id` header used elsewhere for the active subdomain tenant.
 */

export type Fcm = "marex" | "stonex" | "other";
export type SmaStatus = "pending" | "active" | "closed";

export interface SubManagedAccountDto {
  id: string;
  blockId: string;
  customerName: string;
  accountNumber: string;
  subscribedQuantity: number;
  letterOfDirectionUrl: string | null;
  status: SmaStatus;
  createdAt: string;
  updatedAt: string;
}

export interface BlockAccountDto {
  id: string;
  tenantId: string;
  fcm: Fcm;
  fcmAccountNumber: string;
  /** Derived — SUM(subscribedQuantity) over non-closed SMAs. Never write directly. */
  totalQuantity: number;
  /** FCM-reported, manually entered. Never derived/recomputed. */
  marginBalance: number;
  createdAt: string;
  updatedAt: string;
  /** Nested on the list endpoint; absent (fresh block) or empty right after create. */
  smas: SubManagedAccountDto[];
}

export interface BlockAccountsListResponse {
  blocks: BlockAccountDto[];
}

export interface CreateBlockAccountRequest {
  tenantId: string;
  fcm: Fcm;
  fcmAccountNumber: string;
  /** Optional — DB default 0, manually entered/updated later via patch. */
  marginBalance?: number;
}

/** Server rejects direct `totalQuantity` writes — margin/account number only. */
export interface PatchBlockAccountRequest {
  marginBalance?: number;
  fcmAccountNumber?: string;
}

export interface CreateSmaRequest {
  customerName: string;
  accountNumber: string;
  subscribedQuantity: number;
  letterOfDirectionUrl?: string | null;
  /** Optional — server defaults to 'pending'. */
  status?: SmaStatus;
}

export type PatchSmaRequest = Partial<CreateSmaRequest>;

export interface LinkPaperAccountRequest {
  paperTradingAccountId: string;
}

/**
 * Flat, denormalized row — the #70 daily-export interface. `unrealizedPnl` is
 * reserved-null this iteration (mark-price plumbing lands in #70).
 */
export interface SmaExportRow {
  blockId: string;
  tenantId: string;
  fcm: Fcm;
  fcmAccountNumber: string;
  blockTotalQuantity: number;
  blockMarginBalance: number;
  smaId: string;
  customerName: string;
  accountNumber: string;
  subscribedQuantity: number;
  status: SmaStatus;
  letterOfDirectionUrl: string | null;
  /** SUM(positions.realized_pnl) across linked paper-trading accounts; 0 if unlinked. */
  realizedPnl: number;
  /** Reserved — always null this iteration; #70 widens to `number | null`. */
  unrealizedPnl: null;
  openPositionCount: number;
  asOf: string;
}

/** SFWL-013: one row per tenant attempted by a `run-daily-export` call. */
export interface RunDailyExportResult {
  tenantId: string;
  status: "sent" | "failed";
  rowCount: number;
  /** 1 on first-try success/failure, 2 if the single automatic retry ran. */
  attempts: number;
}

/** SFWL-013: per-tenant daily Block/SMA export delivery settings. */
export interface ExportConfig {
  dailyExportRecipients: string[];
  dailyExportTimezone: string;
}

const BLOCK_ACCOUNTS_BASE = `${API_BASE_URL}/api/v1/admin/block-accounts`;

export const blockAccountsApi = {
  /** List blocks with nested SMAs. Omit `tenantId` to list across all tenants. */
  async list(tenantId?: string): Promise<BlockAccountsListResponse> {
    const queryString = buildQueryString(tenantId ? { tenantId } : undefined);
    const response = await fetchWithTimeout(`${BLOCK_ACCOUNTS_BASE}${queryString}`);
    return handleResponse<BlockAccountsListResponse>(response);
  },

  /** Create a block account (tenant-existence checked server-side). */
  async createBlock(data: CreateBlockAccountRequest): Promise<BlockAccountDto> {
    const response = await fetchWithTimeout(BLOCK_ACCOUNTS_BASE, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<BlockAccountDto>(response);
  },

  /** Patch marginBalance and/or fcmAccountNumber. totalQuantity is not writable here. */
  async patchBlock(id: string, data: PatchBlockAccountRequest): Promise<BlockAccountDto> {
    const response = await fetchWithTimeout(`${BLOCK_ACCOUNTS_BASE}/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<BlockAccountDto>(response);
  },

  /** Cascade-deletes the block's SMAs; linked paper-trading accounts survive with sma_id NULL. */
  async deleteBlock(id: string): Promise<void> {
    const response = await fetchWithTimeout(`${BLOCK_ACCOUNTS_BASE}/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) {
      await handleResponse<void>(response);
    }
  },

  /** Add an SMA to a block. Triggers an in-transaction totalQuantity recompute. */
  async addSma(blockId: string, data: CreateSmaRequest): Promise<SubManagedAccountDto> {
    const response = await fetchWithTimeout(`${BLOCK_ACCOUNTS_BASE}/${blockId}/smas`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
    return handleResponse<SubManagedAccountDto>(response);
  },

  /** Patch an SMA (customerName/accountNumber/subscribedQuantity/letterOfDirectionUrl/status). Triggers recompute. */
  async patchSma(
    blockId: string,
    smaId: string,
    data: PatchSmaRequest
  ): Promise<SubManagedAccountDto> {
    const response = await fetchWithTimeout(
      `${BLOCK_ACCOUNTS_BASE}/${blockId}/smas/${smaId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<SubManagedAccountDto>(response);
  },

  /** Removes an SMA. Linked paper-trading accounts have sma_id set NULL, never deleted. */
  async deleteSma(blockId: string, smaId: string): Promise<void> {
    const response = await fetchWithTimeout(
      `${BLOCK_ACCOUNTS_BASE}/${blockId}/smas/${smaId}`,
      { method: "DELETE" }
    );
    if (!response.ok) {
      await handleResponse<void>(response);
    }
  },

  /** Force a full-sum totalQuantity recompute (demo verification / drift repair). */
  async recompute(blockId: string): Promise<BlockAccountDto> {
    const response = await fetchWithTimeout(
      `${BLOCK_ACCOUNTS_BASE}/${blockId}/recompute`,
      { method: "POST" }
    );
    return handleResponse<BlockAccountDto>(response);
  },

  /**
   * Link a paper-trading account to an SMA (N:1 — multiple accounts may link
   * to one SMA). 404 if the account doesn't exist; 409 if it's already linked
   * to a *different* SMA.
   */
  async linkAccount(
    blockId: string,
    smaId: string,
    data: LinkPaperAccountRequest
  ): Promise<SubManagedAccountDto> {
    const response = await fetchWithTimeout(
      `${BLOCK_ACCOUNTS_BASE}/${blockId}/smas/${smaId}/link-account`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<SubManagedAccountDto>(response);
  },

  /** HTTP mirror of the in-process `summaryForExport` service call #70 uses. */
  async exportSummary(tenantId?: string): Promise<SmaExportRow[]> {
    const queryString = buildQueryString(tenantId ? { tenantId } : undefined);
    const response = await fetchWithTimeout(
      `${BLOCK_ACCOUNTS_BASE}/export-summary${queryString}`
    );
    return handleResponse<SmaExportRow[]>(response);
  },

  /**
   * SFWL-013: manually trigger the daily Block/SMA export email. Omit
   * `tenantId` to run every tenant with configured recipients. Bypasses the
   * once-per-tenant-local-day cron guard (demo re-runs).
   */
  async runDailyExport(tenantId?: string): Promise<RunDailyExportResult[]> {
    const response = await fetchWithTimeout(`${BLOCK_ACCOUNTS_BASE}/run-daily-export`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenantId }),
    });
    return handleResponse<RunDailyExportResult[]>(response);
  },

  /** SFWL-013: fetch a tenant's daily export recipient list + timezone. */
  async getExportConfig(tenantId: string): Promise<ExportConfig> {
    const response = await fetchWithTimeout(
      `${BLOCK_ACCOUNTS_BASE}/export-config/${tenantId}`
    );
    return handleResponse<ExportConfig>(response);
  },

  /** SFWL-013: set a tenant's daily export recipient list + timezone. */
  async setExportConfig(tenantId: string, data: ExportConfig): Promise<ExportConfig> {
    const response = await fetchWithTimeout(
      `${BLOCK_ACCOUNTS_BASE}/export-config/${tenantId}`,
      {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
    return handleResponse<ExportConfig>(response);
  },
};
